import type { Delta } from 'quill/core';
import React from 'react';
import Avatar from '../components/Avatar';
import Card, { CardSeparator } from '../components/Card';
import CardHeader from '../components/CardHeader';
import DeltaPreview from '../components/DeltaPreview';
import MetaList, { MetaListItem } from '../components/MetaList';
import Timestamp from '../components/Timestamp';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import renderHighlight from '../utils/search/renderHighlight';
import userPath from '../utils/url/userPath';
import * as styles from './UserResultCard.module.scss';
import UserBadge from './profile/UserBadge';
import UserProfileButtons from './profile/UserProfileButtons';

const USER_FRAGMENT = gql(`
  fragment UserResultCard_user on User {
    email
    insertedAt
    name
    title
    description
    ...Avatar_user
    ...UserProfileButtons_user
    ...UserBadge_user
  }
`);

interface UserResultCardProps {
  description?: Delta | null;
  name?: string | null;
  title?: string | null;
  user: DocumentType<typeof USER_FRAGMENT>;
}

const UserResultCard = ({
  description,
  user,
  ...props
}: UserResultCardProps) => {
  const { insertedAt } = user;

  const name = props.name ? renderHighlight(props.name) : user.name;
  const title = props.title || user.title;
  const main = title || user.email;
  const actualDescription = description || user.description;

  return (
    <Card to={userPath(user)} aria-label={user.name}>
      <CardHeader
        icon={<Avatar user={user} />}
        title={name}
        meta={
          <MetaList>
            {main && <MetaListItem>{renderHighlight(main)}</MetaListItem>}
            {!!insertedAt && (
              <MetaListItem>
                Joined <Timestamp date={insertedAt} />
              </MetaListItem>
            )}
          </MetaList>
        }
      />
      {!!actualDescription && (
        <>
          <CardSeparator />
          <DeltaPreview
            content={actualDescription}
            typography="searchResult"
            maxLines={4}
          />
        </>
      )}
      <div className={styles.footer}>
        <UserProfileButtons size="small" user={user} />
        <UserBadge user={user} />
      </div>
    </Card>
  );
};

export default UserResultCard;
